import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import Hero from "@/components/Hero";
import ServicesGrid from "@/components/ServicesGrid";
import CTABanner from "@/components/CTABanner";
import { useSEO } from "@/lib/seo";
import { ABOUT_HERO, VALUES } from "@/lib/content";

export default function Home() {
  useSEO({
    title: "Bellhouse Law, Practical advice, proven results.",
    description:
      "Bellhouse Law: clear, practical legal advice for property, conveyancing, commercial and business law on the Sunshine Coast.",
  });

  return (
    <div data-testid="home-page">
      <Hero />

      {/* Philosophy / About on Home */}
      <section
        className="section-pad bg-sage-50/60"
        data-testid="philosophy-section"
      >
        <div className="container-narrow">
          <div className="grid grid-cols-1 gap-16 md:grid-cols-12 md:gap-12 lg:gap-20">
            <div className="md:col-span-5">
              <span className="eyebrow">Our approach</span>
              <h2 className="mt-4 text-4xl font-bold leading-[1.05] text-ink md:text-5xl lg:text-[56px]">
                Considered law, without the formality.
              </h2>
            </div>
            <div className="md:col-span-7 space-y-7">
              <p className="text-2xl font-medium leading-snug text-ink md:text-[28px]">
                We started Bellhouse Law to provide the kind of advice we
                always wished we could give, direct, honest, and built around
                the way our clients actually run their lives and businesses.
              </p>
              <p className="text-base leading-relaxed text-ink-soft md:text-lg">
                That means picking up the phone. It means fixed-fee
                transparency on standard matters. It means staff who know your
                name, your file and your context, every time you reach out.
              </p>
              <Link
                to="/about"
                data-testid="philosophy-learn-more"
                className="inline-flex items-center gap-2 text-[12px] font-medium uppercase tracking-[0.18em] text-sage-700 hover:gap-3"
              >
                More about us <ArrowUpRight className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <ServicesGrid />

      {/* Why Bellhouse — 4 value cards */}
      <section
        className="section-pad bg-sage-50/50"
        data-testid="why-bellhouse-section"
      >
        <div className="container-narrow">
          <div className="mb-14 grid grid-cols-1 gap-8 md:grid-cols-12">
            <div className="md:col-span-5">
              <span className="eyebrow">Why Bellhouse</span>
              <h2 className="mt-4 text-4xl font-bold leading-[1.05] text-ink md:text-5xl lg:text-[56px]">
                Four quiet commitments.
              </h2>
            </div>
            <p className="text-base leading-relaxed text-ink-soft md:col-span-6 md:col-start-7 md:text-lg">
              The principles we have built the firm around, and the standards
              you can hold us to.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-8 lg:grid-cols-4">
            {VALUES.map((v, i) => (
              <motion.div
                key={v.title}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.55, delay: i * 0.08 }}
                data-testid={`value-card-${v.title.toLowerCase()}`}
                className="border border-sage-100 bg-white p-8 transition-all duration-500 hover:-translate-y-1 hover:border-sage-300 hover:shadow-[0_24px_60px_-30px_rgba(26,30,25,0.18)]"
              >
                <div className="grid h-9 w-9 place-items-center border border-sage-200 text-sage-700">
                  <Check className="h-4 w-4" strokeWidth={2} />
                </div>
                <div className="mt-6 text-2xl font-bold text-ink">
                  {v.title}.
                </div>
                <p className="mt-4 text-sm leading-relaxed text-ink-soft">
                  {v.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Built on the Coast */}
      <section
        className="bg-bone section-pad"
        data-testid="built-on-coast-section"
      >
        <div className="container-narrow grid grid-cols-1 items-center gap-12 md:grid-cols-12 md:gap-16">
          <div className="md:col-span-6">
            <div className="relative aspect-[4/5] overflow-hidden">
              <img
                src={ABOUT_HERO}
                alt="Coastal cypress trees overlooking the bay"
                className="h-full w-full object-cover"
              />
            </div>
          </div>
          <div className="md:col-span-6">
            <span className="eyebrow">Built on the Coast</span>
            <h2 className="mt-4 text-4xl font-bold leading-tight text-ink md:text-5xl">
              Practical legal services, tailored to your needs.
            </h2>
            <p className="mt-6 text-base leading-relaxed text-ink-soft md:text-lg">
              Whether you&apos;re buying or selling property, leasing commercial
              premises, purchasing a business or seeking employment advice,
              Bellhouse Law provides clear, practical legal solutions tailored
              to your circumstances.
            </p>
            <p className="mt-5 text-base leading-relaxed text-ink-soft md:text-lg">
              We focus on responsive communication, efficient processes and
              commercially sensible advice, so you can move forward with
              confidence.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/services"
                data-testid="editorial-services"
                className="btn-secondary"
              >
                Explore Our Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      <CTABanner />
    </div>
  );
}
